import { useState } from 'react';
import Sheet from '@/components/ui/Sheet';
import { cancelarNaoValidadas, type ModalidadePresencial } from '@/lib/presencial';

interface Props {
  aberto: boolean;
  modalidades: ModalidadePresencial[];
  aoConcluir: () => void;
  aoFechar: () => void;
}

// "Cancelar quem não foi bipado": por modalidade, com confirmação do número.
export default function FolhaCancelar({ aberto, modalidades, aoConcluir, aoFechar }: Props) {
  const [confirmando, setConfirmando] = useState<ModalidadePresencial | null>(null);
  const [cancelando, setCancelando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const comAguardando = modalidades.filter((m) => m.aguardando > 0);

  async function cancelar(m: ModalidadePresencial) {
    setCancelando(true);
    setErro(null);
    try {
      await cancelarNaoValidadas(m.id);
      setConfirmando(null);
      aoConcluir();
      aoFechar();
    } catch {
      setErro('Não foi possível cancelar agora. Tente de novo.');
    } finally {
      setCancelando(false);
    }
  }

  return (
    <Sheet
      aberto={aberto}
      titulo="Cancelar quem não foi bipado"
      aoFechar={() => {
        setConfirmando(null);
        aoFechar();
      }}
    >
      {erro && (
        <p className="mb-3 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-[14px] text-danger-700">
          {erro}
        </p>
      )}

      {comAguardando.length === 0 ? (
        <p className="py-6 text-center text-[15px] text-foreground-600">
          Ninguém aguardando validação agora.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-[14px] text-foreground-600">
            Cancela as inscrições que ainda não foram bipadas nesta modalidade. Quem já tem vaga
            não é afetado.
          </p>
          {comAguardando.map((m) => (
            <div
              key={m.id}
              className="rounded-[16px] border border-background-200 bg-background-50 p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold text-foreground-950">{m.nome}</p>
                  <p className="mt-0.5 text-[13px] text-foreground-500">
                    {m.aguardando} aguardando validação
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmando(m)}
                  className="aperta shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-danger-300 bg-background-50 px-4 py-2 text-[14px] font-semibold text-danger-600 transition-colors hover:bg-danger-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet
        aberto={!!confirmando}
        titulo="Confirmar cancelamento"
        aoFechar={() => setConfirmando(null)}
        zIndex={80}
      >
        {confirmando && (
          <>
            <p className="text-[15px] text-foreground-700">
              Cancelar <strong>{confirmando.aguardando}</strong>{' '}
              {confirmando.aguardando === 1 ? 'inscrição' : 'inscrições'} sem bipagem em{' '}
              <strong>{confirmando.nome}</strong>?
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setConfirmando(null)}
                disabled={cancelando}
                className="aperta h-12 w-full cursor-pointer whitespace-nowrap rounded-[16px] border border-background-300 bg-background-50 text-[15px] font-semibold text-foreground-800 disabled:opacity-60 sm:flex-1"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => cancelar(confirmando)}
                disabled={cancelando}
                className="aperta h-12 w-full cursor-pointer whitespace-nowrap rounded-[16px] bg-danger-500 text-[15px] font-semibold text-white disabled:opacity-60 sm:flex-1"
              >
                {cancelando ? 'Cancelando…' : 'Cancelar inscrições'}
              </button>
            </div>
          </>
        )}
      </Sheet>
    </Sheet>
  );
}